/**
 * When and how the client asks the server for fresh state (NEWS-238, NM-231).
 *
 * This is the *ordering* policy that used to live inside `app.tsx`, where it
 * could not be tested: it reads `document`, drives timers, and sits in a
 * 4,000-line module that imports the whole UI. So the rules around refreshing —
 * skip while hidden, catch up on return, don't spin on an error — were covered
 * only by E2E, and E2E cannot schedule an interleaving.
 *
 * That distinction is the lesson from NEWS-104, which chased an
 * out-of-order-response bug through 288 `repeat-each` executions of the spec
 * that flaked without reproducing it once; a unit test that *chose* the ordering
 * failed deterministically the moment it was written. This module is the same
 * move for the other half: NEWS-104 pinned *which answer wins*, this pins *when
 * we ask*.
 *
 * **NM-231 turned the fixed timer into a long-poll loop.** The old model fired a
 * request every four seconds whether or not anything had changed. Now each
 * refresh long-polls — the server holds it until the state revision moves or a
 * timeout elapses (`refreshState({ wait: true })`) — and the loop re-issues the
 * next the instant the last answers. A visible tab therefore holds roughly one
 * request at a time and updates with push latency; an idle one makes a request
 * only every timeout, not every four seconds.
 *
 * Every ambient dependency is injected so a test can drive the clock and the
 * visibility state directly instead of hoping to observe a held request.
 */

/** How long to wait after a failed refresh before trying again, so a persistent
 * error (a 500, an offline network) does not spin the loop. */
export const POLL_ERROR_BACKOFF_MS = 4000;

export interface PollDeps {
  /**
   * Ask the server for current state, long-polling: the returned promise
   * resolves when the server answers (a change, or its hold timing out).
   * Rejects on a transport error, which the loop backs off from. The `signal`
   * is aborted by `stop()` so a *held* request is cancelled at sign-out rather
   * than completing (and firing a stray follow-up) at a signed-out session
   * (NM-231/NM-41).
   */
  refresh: (signal: AbortSignal) => Promise<void>;
  /** Is the page being displayed right now? */
  isVisible: () => boolean;
  /**
   * Subscribe to visibility changes; used to park while hidden and resume on
   * return. Returns an unsubscribe so the loop can be fully torn down on
   * sign-out (NM-41) — a lingering listener would resume a logged-out session.
   */
  onVisibilityChange: (listener: () => void) => () => void;
  /** Injected so tests need no real timer. Resolves after `ms`. */
  delay: (ms: number) => Promise<void>;
}

/**
 * Start the long-poll loop.
 *
 * **Skipping while hidden is deliberate** — a backgrounded tab holding a request
 * open forever is a real cost on a laptop, and nobody is reading the answer. The
 * loop parks on a promise the visibility listener resolves, so it holds no
 * connection at all while hidden.
 *
 * **The catch-up on becoming visible is free here** — the loop simply resumes
 * and its next refresh answers with whatever changed while away, up to the
 * server's hold timeout. There is no fixed interval left to wait out.
 *
 * **An error backs off rather than spins.** Without a held request there is no
 * natural pace, so a refresh that rejects (a 500, an offline network) is
 * followed by a short delay before the next attempt. A successful long poll
 * needs no delay — the server already held it.
 *
 * Returns a `stop()` that ends the loop and removes the visibility listener
 * (NM-41). A refresh already in flight is left to resolve and then ignored — the
 * same property the interval model had — but no *new* request is issued.
 */
export function startPolling(deps: PollDeps): () => void {
  // A holder object rather than a bare `let`: the flag is only ever cleared from
  // the returned `stop()` closure, and reading it through a property keeps the
  // type checker (and `no-unnecessary-condition`) from narrowing the loop
  // condition to a constant it can't see the closure change.
  const loop = { running: true };
  // Set while the loop is parked waiting to become visible; calling it resumes.
  let resumeFromHidden: (() => void) | null = null;
  // The in-flight refresh's aborter, so `stop()` can cancel a held long poll.
  let inflight: AbortController | null = null;

  const wake = (): void => {
    const resume = resumeFromHidden;
    resumeFromHidden = null;
    if (resume) resume();
  };

  const unsubscribe = deps.onVisibilityChange(() => {
    // Only the way *back* matters: a hidden-going transition has nothing to
    // refresh for, and the top-of-loop check handles parking either way.
    if (deps.isVisible()) wake();
  });

  void (async () => {
    while (loop.running) {
      if (!deps.isVisible()) {
        await new Promise<void>((resolve) => {
          resumeFromHidden = resolve;
        });
        continue;
      }
      const controller = new AbortController();
      inflight = controller;
      try {
        await deps.refresh(controller.signal);
      } catch {
        // Back off so a persistent failure does not spin. A `stop()` during the
        // wait ends the loop on the next `running` check.
        await deps.delay(POLL_ERROR_BACKOFF_MS);
      } finally {
        inflight = null;
      }
    }
  })();

  return () => {
    loop.running = false;
    // Cancel a held long poll so it does not complete — and fire a follow-up
    // request — at a session that has just signed out (NM-231/NM-41).
    inflight?.abort();
    // Unpark a hidden loop so it observes `running === false` and exits.
    wake();
    unsubscribe();
  };
}

/** The dependencies as they are in a browser. */
export function browserPollDeps(refresh: (signal: AbortSignal) => Promise<void>): PollDeps {
  return {
    refresh,
    isVisible: () => document.visibilityState === 'visible',
    onVisibilityChange: (listener) => {
      document.addEventListener('visibilitychange', listener);
      return () => {
        document.removeEventListener('visibilitychange', listener);
      };
    },
    delay: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  };
}
